import React, { useCallback, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminHubPageFrame from '../../components/admin/AdminHubPageFrame';
import AdminFilterBar from '../../components/admin/AdminFilterBar';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import AdminMetricCard from '../../components/admin/AdminMetricCard';
import AdminDataTable from '../../components/admin/AdminDataTable';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminJsonDetails from '../../components/admin/AdminJsonDetails';
import { copyTextToClipboard, exportAdminTableAsCsv, exportAdminTableAsJson, normalizeAdminExportRows } from '../../admin/adminExportUtils';
import { filterAdminTableRows, nextSortState, sortAdminTableRows } from '../../admin/adminTableClient';

const ASYNC_STATUS_KEYS = ['status', 'n'];

export default function AdminOperationsAsyncPage() {
  const { t } = useLanguage();
  const [limit, setLimit] = useState('30');
  const [summaryRes, setSummaryRes] = useState(null);
  const [jobsRes, setJobsRes] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(false);

  const [searchStatus, setSearchStatus] = useState('');
  const [sortStatus, setSortStatus] = useState({ key: null, dir: null });
  const [compactStatus, setCompactStatus] = useState(false);

  const [searchJobs, setSearchJobs] = useState('');
  const [fJobType, setFJobType] = useState('');
  const [fSource, setFSource] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [sortJobs, setSortJobs] = useState({ key: null, dir: null });
  const [compactJobs, setCompactJobs] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(false);
    try {
      const [sRes, jRes] = await Promise.all([
        fetch(buildApiUrl(API_ENDPOINTS.ADMIN_OPERATIONAL_ASYNC_SUMMARY), { credentials: 'include' }),
        fetch(
          `${buildApiUrl(API_ENDPOINTS.ADMIN_OPERATIONAL_ASYNC_RECENT)}?limit=${encodeURIComponent(limit)}`,
          { credentials: 'include' }
        ),
      ]);
      if (!sRes.ok || !jRes.ok) throw new Error('bad');
      setSummaryRes(await sRes.json());
      setJobsRes(await jRes.json());
    } catch {
      setErr(true);
      setSummaryRes(null);
      setJobsRes(null);
    } finally {
      setLoading(false);
    }
  };

  const statusRows = useMemo(() => {
    const summary = summaryRes?.summary || summaryRes;
    const rows = Array.isArray(summary?.by_status) ? summary.by_status : [];
    return rows.map((r, i) => ({
      __rowKey: `${r.status}-${i}`,
      status: r.status,
      n: r.n != null ? String(r.n) : '—',
    }));
  }, [summaryRes]);

  const asyncSummary = useMemo(() => {
    if (!summaryRes) return null;
    return summaryRes.summary != null ? summaryRes.summary : summaryRes;
  }, [summaryRes]);

  const statusCols = useMemo(
    () => [
      { key: 'status', header: t('adminKit.colJobStatus') },
      { key: 'n', header: t('adminKit.colCount') },
    ],
    [t]
  );

  const jobCols = useMemo(
    () => [
      { key: 'job_id', header: 'job_id' },
      { key: 'job_type', header: t('adminKit.colJobType') },
      { key: 'source_project', header: t('adminKit.colSourceProject') },
      { key: 'status', header: t('adminKit.colJobStatus') },
      { key: 'retry_count', header: t('adminKit.colRetries') },
      { key: 'updated_at', header: t('adminKit.colUpdated') },
    ],
    [t]
  );
  const jobKeys = useMemo(() => jobCols.map((c) => c.key), [jobCols]);

  const jobRows = useMemo(() => {
    const jobs = jobsRes?.jobs || jobsRes?.rows || [];
    return (Array.isArray(jobs) ? jobs : []).slice(0, 300);
  }, [jobsRes]);

  const statusFiltered = useMemo(
    () => sortAdminTableRows(filterAdminTableRows(statusRows, ASYNC_STATUS_KEYS, searchStatus, {}), sortStatus.key, sortStatus.dir),
    [statusRows, searchStatus, sortStatus]
  );

  const jobsFiltered = useMemo(
    () =>
      sortAdminTableRows(
        filterAdminTableRows(jobRows, jobKeys, searchJobs, {
          job_type: fJobType,
          source_project: fSource,
          status: fStatus,
        }),
        sortJobs.key,
        sortJobs.dir
      ),
    [jobRows, jobKeys, searchJobs, fJobType, fSource, fStatus, sortJobs]
  );

  const onSortStatus = useCallback((key) => {
    const n = nextSortState(sortStatus.key, sortStatus.dir, key);
    setSortStatus({ key: n.key, dir: n.dir });
  }, [sortStatus]);

  const onSortJobs = useCallback((key) => {
    const n = nextSortState(sortJobs.key, sortJobs.dir, key);
    setSortJobs({ key: n.key, dir: n.dir });
  }, [sortJobs]);

  const crumbs = useMemo(
    () => [
      { label: t('adminHub.navOperations'), to: '/admin/operations/async-jobs' },
      { label: t('adminHub.navAsyncJobs') },
    ],
    [t]
  );

  const hasJobFilters = !!(searchJobs.trim() || fJobType.trim() || fSource.trim() || fStatus.trim());

  return (
    <AdminHubPageFrame titleKey="adminHub.asyncJobsTitle" descKey="adminHub.asyncJobsDesc" breadcrumbItems={crumbs}>
      <section className="admin-section-card">
        <AdminFilterBar>
          <label className="admin-hub-kit-toolbar__label">
            {t('adminHub.asyncLimit')}
            <input className="admin-hub-kit-toolbar__input" value={limit} onChange={(e) => setLimit(e.target.value)} />
          </label>
          <button type="button" className="admin-hub-btn" onClick={load} disabled={loading}>
            {loading ? t('adminPage.loading') : t('adminHub.loadAction')}
          </button>
        </AdminFilterBar>
        {err ? (
          <div className="admin-state admin-state--error" role="status">
            <p>{t('adminHub.loadError')}</p>
          </div>
        ) : null}
        {summaryRes ? (
          <div className="admin-hub-kit-stack">
            <AdminSectionHeader title={t('adminHub.asyncSummaryHeading')} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard label={t('adminKit.asyncPending')} value={asyncSummary?.pending_count ?? '—'} />
              <AdminMetricCard label={t('adminKit.asyncRetryHeavy')} value={asyncSummary?.retry_heavy_count ?? '—'} />
              <AdminMetricCard label={t('adminKit.asyncStuckThreshold')} value={asyncSummary?.stuck_threshold_minutes ?? '—'} />
              <AdminMetricCard
                label={t('adminKit.asyncStuckSample')}
                value={Array.isArray(asyncSummary?.stuck_processing_sample) ? asyncSummary.stuck_processing_sample.length : 0}
              />
            </div>
            <AdminTableToolbar
              search={searchStatus}
              onSearchChange={setSearchStatus}
              filteredCount={statusFiltered.length}
              totalCount={statusRows.length}
              compact={compactStatus}
              onCompactChange={setCompactStatus}
              onExportCsv={() => exportAdminTableAsCsv(statusFiltered, ASYNC_STATUS_KEYS, 'async-by-status')}
              onExportJson={() => exportAdminTableAsJson(statusFiltered, ASYNC_STATUS_KEYS, 'async-by-status')}
              onCopyFilteredJson={() => copyTextToClipboard(JSON.stringify(normalizeAdminExportRows(statusFiltered, ASYNC_STATUS_KEYS)))}
            />
            <AdminDataTable
              columns={statusCols}
              rows={statusFiltered}
              emptyMessage={searchStatus.trim() ? t('adminExport.noResults') : t('adminKit.tableEmpty')}
              emptyVariant={searchStatus.trim() ? 'noResults' : 'default'}
              compact={compactStatus}
              sortable
              sortKey={sortStatus.key}
              sortDir={sortStatus.dir}
              onSortChange={onSortStatus}
              copyRowKeys={ASYNC_STATUS_KEYS}
            />
            <AdminJsonDetails title={t('adminKit.rawJsonToggle')} data={asyncSummary} />
          </div>
        ) : null}
        {jobsRes ? (
          <div className="admin-hub-kit-stack" style={{ marginTop: '1.5rem' }}>
            <AdminSectionHeader title={t('adminHub.asyncRecentHeading')} level={3} />
            <AdminTableToolbar
              search={searchJobs}
              onSearchChange={setSearchJobs}
              filteredCount={jobsFiltered.length}
              totalCount={jobRows.length}
              compact={compactJobs}
              onCompactChange={setCompactJobs}
              onExportCsv={() => exportAdminTableAsCsv(jobsFiltered, jobKeys, 'async-recent-jobs')}
              onExportJson={() => exportAdminTableAsJson(jobsFiltered, jobKeys, 'async-recent-jobs')}
              onCopyFilteredJson={() => copyTextToClipboard(JSON.stringify(normalizeAdminExportRows(jobsFiltered, jobKeys)))}
              filters={[
                { id: 'jt', label: t('adminKit.colJobType'), value: fJobType, onChange: setFJobType },
                { id: 'src', label: t('adminKit.colSourceProject'), value: fSource, onChange: setFSource },
                { id: 'st', label: t('adminKit.colJobStatus'), value: fStatus, onChange: setFStatus },
              ]}
            />
            <AdminDataTable
              columns={jobCols}
              rows={jobsFiltered}
              emptyMessage={hasJobFilters ? t('adminExport.noResults') : t('adminKit.tableEmpty')}
              emptyVariant={hasJobFilters ? 'noResults' : 'default'}
              compact={compactJobs}
              sortable
              sortKey={sortJobs.key}
              sortDir={sortJobs.dir}
              onSortChange={onSortJobs}
              copyRowKeys={jobKeys}
            />
            <AdminJsonDetails title={t('adminKit.rawJsonToggle')} data={jobsRes} />
          </div>
        ) : null}
      </section>
    </AdminHubPageFrame>
  );
}
